import { map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject } from "rxjs";
import { Guia } from "src/app/models/Guia";

import { LeiLookup } from "../../models/lei/lei.lookup";
import { ErrorHandlerService } from "../errorHandler.service";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { OperacaoSignalR } from "src/app/models/OperacaoSignalR";
import { SignalrService } from "../signalr.service";
import { StorageService } from "../storage.service";

@Injectable()
export class UsuarioGuiasService {
  readonly databaseName = "userdata";
  readonly collectionName = "guias";

  private guias: Guia[] = [];
  private guias$ = new BehaviorSubject<Guia[]>(this.guias);

  private indexGuiaAtiva$ = this.guias$.pipe(
    map((items) => items.findIndex((g) => g.ativa))
  );

  private indexGuia$ = this.guias$.pipe(
    map((items) => items.findIndex((g) => g.ativa))
  );

  private guiaAtiva: Guia = null;
  private guiaAtiva$ = new BehaviorSubject<Guia>(this.guiaAtiva);

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private signalrService: SignalrService,
    private storageService: StorageService
  ) {}
  public get valueGuiaAtiva(): Guia {
    return this.guiaAtiva;
  }

  public async fromNuvem(guias: Guia[]): Promise<void> {
    await this.storageService.upsertMany(
      guias,
      this.databaseName,
      this.collectionName,
      false
    );
  }

  public async carregar(): Promise<void> {
    if (this.guias.length === 0) {
      this.guias = await this.listar();
    }

    let commit = false;

    if (!this.guias || this.guias.length === 0) {
      this.guias.push(this.criarGuia());
      commit = true;
    }

    if (this.guias.findIndex((g) => g.ativa) === -1) {
      this.guias[0].ativa = true;
      commit = true;
    }

    if (this.guias.filter((g) => g.ativa).length >= 2) {
      const iProximaGuia = this.guias.findIndex((g) => g.ativa);
      for (const guia of this.guias) {
        guia.ativa = false;
      }

      this.guias[iProximaGuia].ativa = true;
      commit = true;
    }
    if (commit) {
      await this.atualizarVarias();
    }

    this.updateGuias();
    this.guiaAtiva = this.guias.find((g) => g.ativa);
    this.updateGuiaAtiva();
  }

  public async buscar(id: string): Promise<Guia> {
    try {
      const guia = await this.storageService.getByKey<Guia>(
        id,
        this.databaseName,
        this.collectionName
      );
      return guia;
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  public async atualizar(guia: Guia, sync = true): Promise<void> {
    await this.storageService.upsert(
      guia,
      this.databaseName,
      this.collectionName
    );
    if (sync) {
      const mensagem = new OperacaoSignalR();
      mensagem.dados = guia;
      this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Guias);
    }
    const index = this.guias.findIndex((g) => g.id === guia.id);
    if (index === -1) {
      this.guias.push(guia);
    } else {
      this.guias[index] = guia;
    }
  }

  public async atualizarVarias(sync = true): Promise<void> {
    await this.storageService.upsertMany(
      this.guias,
      this.databaseName,
      this.collectionName
    );
    if (sync) {
      const mensagens = this.guias.map((g) => {
        const msg = new OperacaoSignalR();
        msg.dados = g;
        return msg;
      });

      this.signalrService.enviarMensagens(mensagens, EnumTipoObjeto.Guias);
    }
  }

  public async alterarConteudoGuia(id: string, item: LeiLookup): Promise<void> {
    const guiaExiste = this.guias.find((g) => g.idLei === item.id);
    if (guiaExiste) {
      this.alterarGuiaAtiva(guiaExiste.id);
      return;
    }
    const guia = this.guias.find((g) => g.id === id);

    guia.idLei = item.id;
    guia.titulo = item.titulo;

    await this.atualizar(guia);
    await this.carregar();
  }

  public async fecharGuia(id: string): Promise<void> {
    const iGuiaRemover = this.guias.findIndex((g) => g.id === id);

    this.guias[iGuiaRemover].removido = true;

    await this.atualizarVarias();

    this.guias = this.guias.filter((g) => g.removido !== true);

    await this.storageService.delete(
      this.databaseName,
      this.collectionName,
      id
    );

    await this.carregar();
  }

  public async novaGuia(guiaNova: Guia): Promise<void> {
    try {
      const guiaExiste = this.guias.find((g) => g.idLei === guiaNova.idLei);
      if (guiaExiste) {
        this.alterarGuiaAtiva(guiaExiste.id);
        return;
      }

      await this.atualizar(guiaNova);

      this.alterarGuiaAtiva(guiaNova.id);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  public async alterarGuiaAtiva(id: string): Promise<void> {
    try {
      this.guias.forEach((guia) => (guia.ativa = guia.id === id));

      await this.atualizarVarias();
      await this.carregar();
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  updateGuias() {
    this.guias$.next(this.guias);
  }

  getGuias(): Observable<Guia[]> {
    return this.guias$.asObservable();
  }

  getIndexGuiaAtiva(): Observable<number> {
    return this.indexGuiaAtiva$;
  }

  updateGuiaAtiva() {
    this.guiaAtiva$.next(this.guiaAtiva);
  }

  getGuiaAtiva(): Observable<Guia> {
    return this.guiaAtiva$.asObservable();
  }

  private criarGuia(): Guia {
    return new Guia();
  }
  private async listar(): Promise<Guia[]> {
    try {
      const resultado: Guia[] = [];
      const guias = await this.storageService.list<Guia>(
        this.databaseName,
        this.collectionName
      );
      const idsexiste = new Set<string>();

      for (const item of guias) {
        if (!idsexiste.has(item.idLei)) {
          // Adiciona o item ao resultado se o ID ainda não foi visto
          resultado.push(item);
          idsexiste.add(item.idLei); // Marca o ID como visto
        }
      }

      if (!resultado) {
        return [];
      } else {
        return resultado.filter((g) => !g.removido).sort((g) => g.ordem);
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }
}
